/**
 * One of Connect's customers: who they are, every number, email and address
 * known for them and which clients carry each, every client's own record of
 * them, and what happened to them.
 *
 * Connect's name for the person and each client's name for them are shown
 * apart, on purpose: the first is Connect's conclusion from every client's
 * records, the others are the clients' own and never changed by it.
 *
 * Staff may set the name, merge another customer in, split client records off
 * to a new customer (it is somebody else), undo a merge, or erase the customer
 * — which makes Connect forget them, and leaves every client's contact as it is.
 */

import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitMerge, Split, Trash2, Undo2, UserPen } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Confirm,
  DetailField,
  DetailGrid,
  Input,
  Modal,
  PageHeader,
  QueryPending,
  RecordSelect,
  Sections,
  cn,
  formatDate,
  formatDateTime,
  formatNumber,
  humanize,
  toast,
  useCan,
} from '@connect/web-shared';

import { IdentityHistory } from './connect-customers';
import {
  BASE,
  BASIS,
  CONFIDENCE,
  LINK_STATUS,
  identityApi,
  identityKeys,
  showIdentifier,
  type CustomerDetail,
} from './identity-api';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

export function ConnectCustomerPage() {
  const { publicId = '' } = useParams();
  const customer = useQuery({ queryKey: identityKeys.customer(publicId), queryFn: () => identityApi.customer(publicId) });
  if (customer.data === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4">
        <PageHeader title="Customer" breadcrumbs={[{ label: 'Clients' }, { label: 'Customer database', to: '/customers' }]} />
        <QueryPending query={customer} label="Loading the customer" />
      </div>
    );
  }
  return <CustomerView customer={customer.data} />;
}

function CustomerView({ customer }: { customer: CustomerDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [unmerging, setUnmerging] = useState(false);
  const confidence = CONFIDENCE[customer.name_confidence];
  const active = customer.status === 'active';

  const refresh = async () => queryClient.invalidateQueries({ queryKey: identityKeys.all });
  const unmerge = useMutation({
    mutationFn: () => identityApi.unmerge(customer.public_id),
    onSuccess: async () => {
      toast.success('Merge undone', 'The two are kept apart from now on.');
      await refresh();
    },
    onError: (error) => toast.error('Not undone', errorText(error)),
  });
  const erase = useMutation({
    mutationFn: () => identityApi.erase(customer.public_id),
    onSuccess: async () => {
      toast.success('Customer erased', 'Connect will not rebuild them from any client’s contact.');
      await refresh();
      navigate('/customers');
    },
    onError: (error) => toast.error('Not erased', errorText(error)),
  });

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {customer.name ?? 'No name yet'}
            <Badge tone={confidence.tone}>{confidence.label}</Badge>
            {!active ? <Badge>Merged</Badge> : null}
          </span>
        }
        description="Connect’s customer, built from every client’s records. Each client’s own contact is shown as the client keeps it, and is never changed from here."
        breadcrumbs={[{ label: 'Clients' }, { label: 'Customer database', to: '/customers' }, { label: customer.name ?? 'Customer' }]}
        actions={
          <div className="flex flex-wrap gap-2">
            {active ? (
              <Can permission="crm.customer_identity.edit">
                <Button variant="secondary" onClick={() => setNaming(true)}>
                  <UserPen className="size-4" />
                  Name
                </Button>
                <Button variant="secondary" onClick={() => setMerging(true)}>
                  <GitMerge className="size-4" />
                  Merge in…
                </Button>
              </Can>
            ) : (
              <Can permission="crm.customer_identity.edit">
                <Button variant="secondary" onClick={() => setUnmerging(true)}>
                  <Undo2 className="size-4" />
                  Undo merge
                </Button>
              </Can>
            )}
            <Can permission="crm.customer_identity.delete">
              <Button variant="danger" onClick={() => setErasing(true)}>
                <Trash2 className="size-4" />
                Erase
              </Button>
            </Can>
          </div>
        }
      />

      {customer.merged_into ? (
        <Alert tone="info" title="Merged into another customer">
          Everything known of this person is now on{' '}
          <RouterLink to={`/customers/${customer.merged_into.public_id}`} className="text-accent underline">
            {customer.merged_into.name ?? 'that customer'}
          </RouterLink>
          . Undo the merge if they are different people.
        </Alert>
      ) : null}

      <Sections
        label="Customer"
        items={[
          { id: 'overview', label: 'Overview', content: <Overview customer={customer} /> },
          {
            id: 'identifiers',
            label: 'Numbers & emails',
            badge: <span className="text-2xs text-faint">{customer.identifiers.length}</span>,
            content: <Identifiers customer={customer} />,
          },
          {
            id: 'addresses',
            label: 'Addresses',
            badge: <span className="text-2xs text-faint">{customer.addresses.length}</span>,
            content: <Addresses customer={customer} />,
          },
          {
            id: 'records',
            label: 'Client records',
            badge: <span className="text-2xs text-faint">{customer.link_count}</span>,
            content: <Records customer={customer} />,
          },
          { id: 'history', label: 'History', content: <IdentityHistory customer={customer.public_id} /> },
        ]}
      />

      <NameDialog open={naming} onClose={() => setNaming(false)} customer={customer} />
      <MergeDialog open={merging} onClose={() => setMerging(false)} customer={customer} />
      <Confirm
        open={unmerging}
        onClose={() => setUnmerging(false)}
        title="Undo this merge?"
        message="What this customer brought to the merge goes back to it, and the two are kept apart from now on: Connect will not merge them or ask about them again."
        confirmLabel="Undo merge"
        onConfirm={async () => {
          setUnmerging(false);
          await unmerge.mutateAsync().catch(() => undefined);
        }}
      />
      <Confirm
        open={erasing}
        onClose={() => setErasing(false)}
        title={`Erase ${customer.name ?? 'this customer'}?`}
        message="Connect forgets this person: their numbers, emails, addresses, links and history. Connect will not rebuild them from any client’s contact. Every client’s own contact of them stays exactly as it is — they are the clients’ data. This cannot be undone."
        confirmLabel="Erase"
        variant="danger"
        onConfirm={async () => {
          setErasing(false);
          await erase.mutateAsync().catch(() => undefined);
        }}
      />
    </div>
  );
}

function Overview({ customer }: { customer: CustomerDetail }) {
  const confidence = CONFIDENCE[customer.name_confidence];
  return (
    <Card>
      <CardBody>
        <DetailGrid>
          <DetailField label="Name">{customer.name}</DetailField>
          <DetailField label="How sure">
            <Badge tone={confidence.tone}>{confidence.label}</Badge>
          </DetailField>
          <DetailField label="Clients">{formatNumber(customer.client_count)}</DetailField>
          <DetailField label="Client records">{formatNumber(customer.link_count)}</DetailField>
          <DetailField label="Open cases">{customer.open_reviews ? formatNumber(customer.open_reviews) : 'None'}</DetailField>
          <DetailField label="First seen">{formatDate(customer.first_seen_at)}</DetailField>
          <DetailField label="Last seen">{formatDate(customer.last_seen_at)}</DetailField>
          <DetailField label="Merged in">
            {customer.merged_from.length
              ? customer.merged_from.map((m) => (
                  <RouterLink key={m.public_id} to={`/customers/${m.public_id}`} className="mr-2 text-accent hover:underline">
                    {m.name ?? 'No name'}
                  </RouterLink>
                ))
              : null}
          </DetailField>
        </DetailGrid>
        <p className="mt-4 text-xs text-muted">
          The name is chosen from what the clients call this person — the name most clients agree on — and is never
          written onto any client’s contact. {customer.name_locked ? 'Staff set it, so Connect leaves it alone.' : ''} Connect
          keeps this customer, and each number, email and address, only while a client keeps it.
        </p>
      </CardBody>
    </Card>
  );
}

function Identifiers({ customer }: { customer: CustomerDetail }) {
  if (customer.identifiers.length === 0) {
    return (
      <Card>
        <CardBody className="text-sm text-muted">No number or email is known.</CardBody>
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <CardHeader title="Numbers & emails" description="Each belongs to this customer alone across every client. Verified means it demonstrably reached a person." />
      <ul className="divide-y divide-line">
        {customer.identifiers.map((identifier) => (
          <li key={identifier.public_id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
            <div className="space-y-1">
              <p className={cn('text-sm font-medium text-heading', identifier.kind === 'mobile' && 'font-mono')}>
                {showIdentifier(identifier)}
              </p>
              <p className="text-xs text-muted">
                {identifier.carried_by
                  .filter((c) => c.active)
                  .map((c) => `${c.client.name}${c.role === 'secondary' ? ' · other number of theirs' : ''}`)
                  .join(', ')}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 text-2xs text-faint">
              {identifier.status === 'verified' ? (
                <Badge tone="success">Verified {identifier.verified_via ? `· ${humanize(identifier.verified_via)}` : ''}</Badge>
              ) : (
                <Badge>Seen</Badge>
              )}
              <span>
                Theirs since {formatDate(identifier.held_since)}
                {identifier.verified_at ? ` · last active ${formatDate(identifier.verified_at)}` : ''}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Addresses({ customer }: { customer: CustomerDetail }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader title="Addresses" description="Never used to match people — families and offices share one." />
      {customer.addresses.length === 0 ? (
        <CardBody className="text-sm text-muted">No address is known.</CardBody>
      ) : (
        <ul className="divide-y divide-line">
          {customer.addresses.map((address) => (
            <li key={address.public_id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 text-sm">
              <span>
                {[address.line, address.city, address.state, address.postal_code, address.country].filter(Boolean).join(', ')}
              </span>
              <span className="text-2xs text-faint">
                {formatNumber(address.sources)} record{address.sources === 1 ? '' : 's'} · seen{' '}
                {formatDate(address.last_seen_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Records({ customer }: { customer: CustomerDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canSplit = useCan('crm.customer_identity.edit') && customer.status === 'active';
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const split = useMutation({
    mutationFn: () => identityApi.split(customer.public_id, [...picked]),
    onSuccess: async (created) => {
      toast.success('Split off', 'The chosen records are a new customer now.');
      setPicked(new Set());
      await queryClient.invalidateQueries({ queryKey: identityKeys.all });
      navigate(`/customers/${created.public_id}`);
    },
    onError: (error) => toast.error('Not split', errorText(error)),
  });
  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Client records"
        description="Each client’s own contact of this person, as the client keeps it. A record its client deleted is listed without its details; when the last client deletes theirs, Connect forgets this person."
        actions={
          canSplit && picked.size > 0 ? (
            <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
              <Split className="size-4" />
              Split off {picked.size}
            </Button>
          ) : null
        }
      />
      <ul className="divide-y divide-line">
        {customer.links.map((link) => {
          const status = LINK_STATUS[link.status];
          return (
            <li key={link.public_id} className="flex flex-wrap items-start gap-3 px-4 py-3">
              {canSplit ? (
                <Checkbox
                  aria-label={`Choose ${link.client.name}’s record`}
                  checked={picked.has(link.public_id)}
                  onChange={() => toggle(link.public_id)}
                />
              ) : null}
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="text-sm">
                  <RouterLink to={`/clients/${link.client.public_id}?tab=contacts`} className="font-medium text-accent hover:underline">
                    {link.client.name}
                  </RouterLink>
                  <span className="ml-1.5 font-mono text-2xs text-faint">{link.client.code}</span>
                </p>
                <p className="text-sm text-heading">“{link.contact?.name ?? link.observed_name ?? '—'}”</p>
                {link.contact ? (
                  <p className="font-mono text-xs text-muted">{[link.contact.mobile, link.contact.email].filter(Boolean).join(' · ') || '—'}</p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-1 text-2xs text-faint">
                <Badge tone={status.tone}>{status.label}</Badge>
                <span>
                  {BASIS[link.basis]}
                  {link.locked ? ' · kept here by staff' : ''}
                </span>
                <span>
                  {link.ended_at ? `Ended ${formatDateTime(link.ended_at)}` : `Since ${formatDate(link.linked_at)}`}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      <Confirm
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Split off ${picked.size} record${picked.size === 1 ? '' : 's'}?`}
        message="They become a new customer — somebody else — and take the numbers and emails only they carry. The two are kept apart from now on. No client’s contact changes."
        confirmLabel="Split off"
        onConfirm={async () => {
          setConfirming(false);
          await split.mutateAsync().catch(() => undefined);
        }}
      />
    </Card>
  );
}

function NameDialog({ open, onClose, customer }: { open: boolean; onClose: () => void; customer: CustomerDetail }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(customer.name ?? '');
  const save = useMutation({
    mutationFn: (value: string | null) => identityApi.setName(customer.public_id, value),
    onSuccess: async () => {
      toast.success('Name saved');
      onClose();
      await queryClient.invalidateQueries({ queryKey: identityKeys.all });
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="The customer’s name"
      description="Set by staff, Connect keeps it whatever the clients call them. No client’s contact is renamed."
      footer={
        <>
          {customer.name_locked ? (
            <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate(null)}>
              Let Connect choose
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate(name.trim())}>
            Save
          </Button>
        </>
      }
    >
      {save.isError ? <Alert tone="danger">{errorText(save.error) ?? 'Not saved.'}</Alert> : null}
      <Input label="Name" value={name} maxLength={160} onChange={(event) => setName(event.target.value)} />
    </Modal>
  );
}

function MergeDialog({ open, onClose, customer }: { open: boolean; onClose: () => void; customer: CustomerDetail }) {
  const queryClient = useQueryClient();
  const [other, setOther] = useState<string | null>(null);
  const merge = useMutation({
    mutationFn: () => identityApi.merge(customer.public_id, other!),
    onSuccess: async () => {
      toast.success('Merged', 'Undo it from the other customer’s page if it was wrong.');
      setOther(null);
      onClose();
      await queryClient.invalidateQueries({ queryKey: identityKeys.all });
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge another customer into this one"
      description="Its numbers, emails, addresses and client records join this customer. Every move is written down, so the merge can be undone."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={merge.isPending} disabled={!other} onClick={() => merge.mutate()}>
            Merge
          </Button>
        </>
      }
    >
      {merge.isError ? <Alert tone="danger">{errorText(merge.error) ?? 'Not merged.'}</Alert> : null}
      <p className="mb-1.5 text-xs font-medium text-body">The same person</p>
      <RecordSelect
        endpoint={BASE}
        value={other}
        onChange={(value) => setOther(value)}
        toOption={(row) => ({
          value: String(row.public_id),
          label: `${String(row.name ?? 'No name')} · ${[...((row.mobiles as string[] | undefined) ?? []), ...((row.emails as string[] | undefined) ?? [])].slice(0, 2).join(', ')}`,
        })}
        placeholder="Search by name, mobile or email"
      />
    </Modal>
  );
}
